// The version selector beside Your list (REQ-2026-09-25-06; module 04 D234, B583 to B585 built the server half).
//
// Mike, 2026-09-25: "a selector the same as claude with the ability to select models ... So 'Cobrain 0.9' But a
// user can roll back on 0.8 for example if they found that it worked better for them". D234 (his, 2026-09-20)
// already settled the rest: the newest version is a person's the moment it ships, and they may go back to any of
// the last three this build can run. The button names the version their list is built by; the menu lists those
// versions, newest first, each with its headline; choosing one is their own choice (no admin, no view-as, the
// policies on ranking.brain_choice) and their list is rebuilt at once by the server.
//
// OX17 / DX4 narrows this existing picker to access.version.picker, never under View as.
// Nothing is drawn until the store has released a version: no placeholder beside the title.
import { useAccess } from '../parts/access.js';
import { useViewing } from '../view-as.js';
import { ActionButton } from '../rails/ActionButton.js';
import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import type { Store } from '../api.js';
import { formatDate } from '../labels.js';
import { useLiveRefresh } from '../live.js';
import { brainVersions, menuRows, type Versions } from './api.js';

/** The rebuild is a whole pass on the server (about 95 seconds on 25 Sep); the list is read again while it runs. */
const REREAD_MS = [15_000, 45_000, 90_000, 150_000];

export function VersionPicker(props: { stores: Store[]; onChosen: () => void }) {
  const access = useAccess();
  const viewing = useViewing();
  return access?.version.picker && !access.viewing && !viewing
    ? <KeeperVersionPicker key={access.store.tenantId} {...props} /> : null;
}

function KeeperVersionPicker({ stores, onChosen }: { stores: Store[]; onChosen: () => void }) {
  const home = stores.find((s) => s.kind === 'commercial') ?? stores[0];
  const [v, setV] = useState<Versions | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const root = useRef<HTMLDivElement>(null);
  const timers = useRef<number[]>([]);

  const read = useCallback(() => {
    if (!home) return;
    brainVersions.list(home.tenantId).then(setV, () => { /* no selector rather than a broken one */ });
  }, [home?.tenantId]);
  useEffect(() => { read(); }, [read]);
  // REQ-2026-09-25-10: a version released while the page is open shows when the person comes back to it.
  useLiveRefresh(read);
  useEffect(() => () => { timers.current.forEach((t) => window.clearTimeout(t)); }, []);

  // Closes on a click anywhere else, as Claude's own menu does.
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (root.current && !root.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  if (!home || !v || v.versions.length === 0 || !v.mine) return null;
  const rows = menuRows(v);

  const choose = async (label: string) => {
    if (label === v.mine) { setOpen(false); return; }
    setBusy(label); setError(null);
    try {
      // One number for Cobrain's judgement (D234): the choice is made in each store on the page that offers it.
      for (const s of stores) {
        const there = s.tenantId === home.tenantId ? v : await brainVersions.list(s.tenantId).catch(() => null);
        if (there && there.offered.includes(label)) await brainVersions.choose(s.tenantId, label);
      }
      setV({ ...v, mine: label, versions: v.versions.map((r) => ({ ...r, isMine: r.label === label })) });
      setOpen(false);
      timers.current.forEach((t) => window.clearTimeout(t));
      timers.current = REREAD_MS.map((ms) => window.setTimeout(() => { onChosen(); read(); }, ms));
      onChosen();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    // The menu keeps its keys: triage's own (J, K, the numbers) must not act on the list behind it.
    e.stopPropagation();
    if (e.key === 'Escape') { setOpen(false); root.current?.querySelector<HTMLButtonElement>('.ver-btn')?.focus(); return; }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const items = Array.from(root.current?.querySelectorAll<HTMLButtonElement>('.ver-row') ?? []);
    const at = items.indexOf(document.activeElement as HTMLButtonElement);
    const next = e.key === 'ArrowDown' ? (at + 1) % items.length : (at - 1 + items.length) % items.length;
    items[next]?.focus();
  };

  return (
    <div className="ver" ref={root} onKeyDown={open ? onKey : undefined}>
      <button type="button" className="ver-btn" aria-haspopup="menu" aria-expanded={open}
              onClick={() => { setOpen((o) => !o); setError(null); }}>
        Cobrain {v.mine}<svg className="ver-caret" viewBox="0 0 10 6" aria-hidden="true"><path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.6" /></svg>
      </button>
      {open ? (
        <div className="ver-menu" role="menu" aria-label="Versions of Cobrain">
          {rows.map((r) => (
            <ActionButton key={r.label} type="button" role="menuitemradio" aria-checked={r.label === v.mine}
                    className={r.label === v.mine ? 'ver-row on' : 'ver-row'} disabled={busy !== null || !r.canRun}
                    onClick={() => { void choose(r.label); }}>
              <span className="ver-name">
                Cobrain {r.label}
                {r.isNewest ? <span className="ver-tag">Newest</span> : null}
                <span className="ver-date">{formatDate(r.releasedOn)}</span>
              </span>
              <span className="ver-head">{r.headline}</span>
              {r.label === v.mine ? <svg className="ver-tick" viewBox="0 0 12 10" aria-hidden="true"><path d="M1 5l3.5 3.5L11 1" fill="none" stroke="currentColor" strokeWidth="1.8" /></svg> : null}
              {busy === r.label ? <span className="ver-busy">Switching</span> : null}
            </ActionButton>
          ))}
          {error ? <p className="ver-error" role="alert">{error}</p> : null}
          <p className="ver-note">Going back changes how Cobrain judges your list, not what it has read. Your list is rebuilt straight away.</p>
        </div>
      ) : null}
    </div>
  );
}
